import type { SceneSlot } from "./types.js";

export interface ArtifactSceneContract {
  artifactKind: string;
  layerKind: string;
  slot: SceneSlot;
  endpointKey: string;
  endpointAliases?: readonly string[];
}

export const ARTIFACT_SCENE_CONTRACT = {
  // G26 — XYZ raster basemaps (OSM, Mapbox-style templates). Producers may
  // emit `endpoints.xyz`; intake normalises to `endpoints.tiles` for engines.
  xyz: {
    artifactKind: "xyz",
    layerKind: "raster-xyz",
    slot: "basemaps",
    endpointKey: "tiles",
    endpointAliases: ["xyz", "url"],
  },
  cog: { artifactKind: "cog", layerKind: "raster", slot: "realityData", endpointKey: "cog" },
  pmtiles: {
    artifactKind: "pmtiles",
    layerKind: "vector",
    slot: "featureLayers",
    endpointKey: "pmtiles",
  },
  "pmtiles-vector": {
    artifactKind: "pmtiles-vector",
    layerKind: "vector",
    slot: "featureLayers",
    endpointKey: "pmtiles",
  },
  "pmtiles-raster": {
    artifactKind: "pmtiles-raster",
    layerKind: "raster",
    slot: "realityData",
    endpointKey: "tiles",
    endpointAliases: ["pmtiles"],
  },
  geojson: {
    artifactKind: "geojson",
    layerKind: "vector",
    slot: "featureLayers",
    endpointKey: "features",
  },
  "3dtiles": {
    artifactKind: "3dtiles",
    layerKind: "3dtiles",
    slot: "realityData",
    endpointKey: "tileset3d",
  },
  "cityjson-3dtiles": {
    artifactKind: "cityjson-3dtiles",
    layerKind: "3dtiles",
    slot: "realityData",
    endpointKey: "tileset3d",
  },
  glb: { artifactKind: "glb", layerKind: "bim", slot: "engineeringModels", endpointKey: "model" },
  "quantized-mesh": {
    artifactKind: "quantized-mesh",
    layerKind: "terrain",
    slot: "terrain",
    endpointKey: "terrain",
  },
  "terrain-cog": {
    artifactKind: "terrain-cog",
    layerKind: "terrain",
    slot: "terrain",
    endpointKey: "terrain",
    endpointAliases: ["cog"],
  },
  "terrain-hillshade-cog": {
    artifactKind: "terrain-hillshade-cog",
    layerKind: "raster",
    slot: "realityData",
    endpointKey: "cog",
  },
  "terrain-slope-cog": {
    artifactKind: "terrain-slope-cog",
    layerKind: "raster",
    slot: "realityData",
    endpointKey: "cog",
  },
  "terrain-aspect-cog": {
    artifactKind: "terrain-aspect-cog",
    layerKind: "raster",
    slot: "realityData",
    endpointKey: "cog",
  },
  "terrain-ndsm-cog": {
    artifactKind: "terrain-ndsm-cog",
    layerKind: "raster",
    slot: "realityData",
    endpointKey: "cog",
  },
  "terrain-contour-pmtiles": {
    artifactKind: "terrain-contour-pmtiles",
    layerKind: "vector",
    slot: "featureLayers",
    endpointKey: "pmtiles",
  },
  copc: {
    artifactKind: "copc",
    layerKind: "pointcloud",
    slot: "realityData",
    endpointKey: "copc",
    endpointAliases: ["pointcloud"],
  },
  "potree-v2-octree": {
    artifactKind: "potree-v2-octree",
    layerKind: "pointcloud",
    slot: "realityData",
    endpointKey: "potree",
  },
  "dem-contours": {
    artifactKind: "dem-contours",
    layerKind: "vector",
    slot: "overlays",
    endpointKey: "features",
  },
  "panorama-pose-v1": {
    artifactKind: "panorama-pose-v1",
    layerKind: "panorama",
    slot: "overlays",
    endpointKey: "manifest",
    endpointAliases: ["model"],
  },
  "panorama-bubble-v1": {
    artifactKind: "panorama-bubble-v1",
    layerKind: "panorama-bubble",
    slot: "overlays",
    endpointKey: "stations",
  },
  "sensor-stream-frame-v1": {
    artifactKind: "sensor-stream-frame-v1",
    layerKind: "sensor/timeseries",
    slot: "overlays",
    endpointKey: "stream",
  },
  "ogc-features-snapshot": {
    artifactKind: "ogc-features-snapshot",
    layerKind: "vector",
    slot: "featureLayers",
    endpointKey: "features",
    endpointAliases: ["pmtiles"],
  },
  "mosaic-json": {
    artifactKind: "mosaic-json",
    layerKind: "raster",
    slot: "realityData",
    endpointKey: "tiles",
    endpointAliases: ["mosaic"],
  },
  "imagery-basemap": {
    artifactKind: "imagery-basemap",
    layerKind: "raster",
    slot: "basemaps",
    endpointKey: "cog",
    endpointAliases: ["tiles"],
  },
  "imagery-basemap-pmtiles": {
    artifactKind: "imagery-basemap-pmtiles",
    layerKind: "raster",
    slot: "basemaps",
    endpointKey: "pmtiles",
    endpointAliases: ["tiles"],
  },
  "format-hint": {
    artifactKind: "format-hint",
    layerKind: "metadata",
    slot: "overlays",
    endpointKey: "manifest",
  },
} as const satisfies Record<string, ArtifactSceneContract>;

export type ArtifactKind = keyof typeof ARTIFACT_SCENE_CONTRACT;

export function contractForArtifactKind(kind: ArtifactKind): ArtifactSceneContract {
  return ARTIFACT_SCENE_CONTRACT[kind];
}
